import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { contratosApi, municipiosApi, referenciaApi, entidadesApi, exportApi, zonasApi } from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Search, FileStack, MapPinned, CalendarClock, Building2, ListFilter, Plus, CheckCircle2, AlertCircle, Download, Pencil, Trash2, ExternalLink, Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ui/ConfirmDeleteDialog';
import { useDebounce } from '../hooks/useDebounce';
import { useSortable } from '../hooks/useSortable';
import { SortableHeader } from '../components/ui/SortableHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRow } from '../components/ui/SkeletonRow';
import { formatFecha } from '../utils/dateFormat';

const ESTADOS = [
  { v: '',             l: 'Todos los estados' },
  { v: 'prospecto',    l: 'Prospecto' },
  { v: 'en_licitacion',l: 'En licitación' },
  { v: 'activo',       l: 'Activo' },
  { v: 'prorrogado',   l: 'Prorrogado' },
  { v: 'finalizado',   l: 'Finalizado' },
];
const ESTADO_STYLE = {
  prospecto:     'bg-blue-50 border-blue-200 text-blue-700',
  en_licitacion: 'bg-amber-50 border-amber-200 text-amber-700',
  activo:        'bg-emerald-50 border-emerald-200 text-emerald-700',
  prorrogado:    'bg-sky-50 border-sky-200 text-sky-700',
  finalizado:    'bg-ink-100 border-ink-200 text-ink-500',
};

const ContratosGlobal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canExport = user?.rol === 'admin' || user?.rol === 'dir_comercial';

  // --- Lista contratos ---
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: '', estado: '', zona_id: '', provincia: '', municipio_ine: '', tipo_gestion: '', gestora_id: '' });
  const [zonas, setZonas] = useState([]);
  const [provincias, setProvincias] = useState([]);

  // --- Dialog Evidencias (fuentes verificadas iter29) ---
  const [evidenciasDialog, setEvidenciasDialog] = useState(false);
  const [evidenciasContrato, setEvidenciasContrato] = useState(null);

  const abrirEvidencias = (c) => {
    setEvidenciasContrato(c);
    setEvidenciasDialog(true);
  };

  // --- Dialog nuevo contrato desde mapa ---
  const [contratoDialog, setContratoDialog] = useState(false);
  const [gestoras, setGestoras] = useState([]);
  const [contratoForm, setContratoForm] = useState({
    gestora_id: '', nombre: '', codigo_interno: '',
    fecha_inicio: '', fecha_fin: '', estado: 'activo', tipo_servicio: 'agua_potable',
    proxima_licitacion_fecha: '', licitacion_gestion_notas: '', notas: '',
  });
  const [contratoMunis, setContratoMunis] = useState([]);
  const [contratoErr, setContratoErr] = useState('');
  const [confirmDel, setConfirmDel] = useState({ open: false, item: null });
  const qDebounced = useDebounce(filters.q, 300);

  useEffect(() => {
    (async () => {
      try { setContratos(await contratosApi.list({ limit: 500 })); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
      try { setProvincias(await referenciaApi.provincias()); } catch {}
      try {
        const ents = await entidadesApi.list({ tipo: 'gestora', limit: 1000 });
        setGestoras(ents);
      } catch {}
      try { setZonas(await zonasApi.list()); } catch {}
    })();
  }, []);

  // Provincias filtradas por la zona seleccionada (cascada)
  const provinciasZona = useMemo(() => {
    if (!filters.zona_id) return null; // null → todas
    const z = zonas.find((x) => x.id === filters.zona_id);
    return z ? (z.provincias || []) : [];
  }, [filters.zona_id, zonas]);

  // Municipios disponibles según provincia seleccionada (extraídos de los contratos cargados)
  const municipiosOpciones = useMemo(() => {
    const map = new Map();
    contratos.forEach((c) => {
      (c.municipios_detalle || []).forEach((m) => {
        if (!filters.provincia || m.provincia === filters.provincia) {
          if (!map.has(m.codigo_ine)) map.set(m.codigo_ine, m);
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [contratos, filters.provincia]);

  const filtered = useMemo(() => {
    const hoy = new Date();
    const umbrales = { '12': 365, '24': 730, '36': 1095 };
    return contratos.filter((c) => {
      if (filters.estado && c.estado !== filters.estado) return false;
      if (filters.tamano && c.tamano !== filters.tamano) return false;

      // Filtros cascada zona → provincia → municipio (sobre municipios_detalle del contrato)
      const munis = c.municipios_detalle || [];
      if (provinciasZona !== null) {
        const enZona = munis.some((m) => provinciasZona.includes(m.provincia));
        if (!enZona) return false;
      }
      if (filters.provincia) {
        const enProv = munis.some((m) => m.provincia === filters.provincia);
        if (!enProv) return false;
      }
      if (filters.municipio_ine) {
        const enMuni = munis.some((m) => m.codigo_ine === filters.municipio_ine);
        if (!enMuni) return false;
      }

      if (filters.vence) {
        const dias = umbrales[filters.vence];
        if (!c.fecha_fin) return false;
        const fin = new Date(c.fecha_fin);
        const diffDias = (fin - hoy) / (1000 * 60 * 60 * 24);
        if (diffDias < 0 || diffDias > dias) return false;
      }
      if (filters.tipo_gestion && (c.tipo_gestion || 'desconocido') !== filters.tipo_gestion) return false;
      if (filters.gestora_id && c.gestora_id !== filters.gestora_id) return false;
      if (qDebounced) {
        const q = qDebounced.toLowerCase();
        const hay = `${c.nombre} ${c.gestora_nombre || ''} ${c.codigo_interno || ''} ${(c.municipios_detalle || []).map((m) => m.nombre).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [contratos, filters, provinciasZona, qDebounced]);

  // Ordenación cliente (M3)
  const { sorted, sortKey, sortDir, toggle } = useSortable(
    filtered,
    'nombre',
    'asc',
    {
      gestora: (c) => c.gestora_nombre || '',
      municipios: (c) => (c.municipios_detalle || []).length,
      servicio: (c) => c.tipo_servicio || '',
      tamano: (c) => c.habitantes_gestionados ?? 0,
      fecha_fin: (c) => c.fecha_fin || '',
    }
  );

  const abrirEditarContrato = (c) => {
    setContratoForm({
      id: c.id,
      gestora_id: c.gestora_id || '',
      nombre: c.nombre || '',
      codigo_interno: c.codigo_interno || '',
      fecha_inicio: c.fecha_inicio || '',
      fecha_fin: c.fecha_fin || '',
      estado: c.estado || 'activo',
      tipo_servicio: c.tipo_servicio || 'agua_potable',
      tiene_prorroga: !!c.tiene_prorroga,
      prorroga_anos: c.prorroga_anos ?? '',
      habitantes_gestionados: c.habitantes_gestionados ?? '',
      proxima_licitacion_fecha: c.proxima_licitacion_fecha || '',
      licitacion_gestion_notas: c.licitacion_gestion_notas || '',
      notas: c.notas || '',
    });
    const munis = (c.municipios_detalle || []).map((m) => ({
      codigo_ine: m.codigo_ine, nombre: m.nombre, provincia: m.provincia,
    }));
    setContratoMunis(munis);
    setContratoErr('');
    setContratoDialog(true);
  };

  const borrarContrato = (c) => setConfirmDel({ open: true, item: c });
  const confirmarBorrarContrato = async () => {
    try {
      await contratosApi.remove(confirmDel.item.id);
      toast.success('Contrato borrado');
      const cs = await contratosApi.list({ limit: 500 });
      setContratos(cs);
    } catch (e) { toast.error(e.message); }
  };

  const guardarContrato = async (ev) => {
    ev?.preventDefault?.();
    setContratoErr('');
    if (!contratoForm.gestora_id) { setContratoErr('Selecciona la gestora'); return; }
    if (!contratoForm.nombre.trim()) { setContratoErr('El nombre es obligatorio'); return; }
    try {
      const payload = {
        gestora_id: contratoForm.gestora_id,
        nombre: contratoForm.nombre,
        codigo_interno: contratoForm.codigo_interno || null,
        fecha_inicio: contratoForm.fecha_inicio || null,
        fecha_fin: contratoForm.fecha_fin || null,
        estado: contratoForm.estado,
        tipo_servicio: contratoForm.tipo_servicio || 'agua_potable',
        tiene_prorroga: !!contratoForm.tiene_prorroga,
        prorroga_anos: contratoForm.prorroga_anos === '' ? null : parseInt(contratoForm.prorroga_anos, 10),
        habitantes_gestionados: contratoForm.habitantes_gestionados === '' ? null : parseInt(contratoForm.habitantes_gestionados, 10),
        proxima_licitacion_fecha: contratoForm.proxima_licitacion_fecha || null,
        licitacion_gestion_notas: contratoForm.licitacion_gestion_notas || null,
        notas: contratoForm.notas || null,
        municipios_ine: contratoMunis.map((m) => m.codigo_ine),
      };
      if (contratoForm.id) {
        await contratosApi.update(contratoForm.id, payload);
        toast.success('Contrato actualizado');
      } else {
        await contratosApi.create(payload);
        toast.success('Contrato creado');
      }
      setContratoDialog(false);
      const [cs] = await Promise.all([contratosApi.list()]);
      setContratos(cs);
    } catch (e) { setContratoErr(e.message); }
  };

  return (
    <div data-testid="contratos-global">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900">Contratos de gestión</h1>
          <p className="text-ink-500 mt-1">
            Listado de contratos operados por las gestoras. Para el mapa visual de cobertura municipal, ve a <a href="/mapa-municipios" className="text-brand-600 underline hover:text-brand-700">Mapa de municipios</a>.
          </p>
        </div>
        {canExport && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                setContratoForm({
                  id: null,
                  gestora_id: '', nombre: '', codigo_interno: '',
                  fecha_inicio: '', fecha_fin: '', estado: 'activo', tipo_servicio: 'agua_potable',
                  tiene_prorroga: false, prorroga_anos: '', habitantes_gestionados: '',
                  proxima_licitacion_fecha: '', licitacion_gestion_notas: '', notas: '',
                });
                setContratoMunis([]);
                setContratoErr('');
                setContratoDialog(true);
              }}
              className="bg-brand-500 hover:bg-brand-600 text-white"
              data-testid="btn-nuevo-contrato-global"
            >
              <Plus className="w-4 h-4 mr-2" /> Nuevo contrato
            </Button>
            <Button
              variant="outline"
              onClick={async () => { try { await exportApi.descargar('contratos'); toast.success('CSV descargado'); } catch (e) { toast.error(e.message); } }}
              data-testid="btn-export-contratos"
            >
              <Download className="w-4 h-4 mr-2" /> Exportar CSV
            </Button>
          </div>
        )}
      </div>

      <>
        <Card className="p-4 mb-6 border-ink-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                <Input
                  placeholder="Buscar por nombre, gestora, municipio…"
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  className="pl-9"
                  data-testid="contratos-filter-q"
                />
              </div>
              <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700" value={filters.estado} onChange={(e) => setFilters({ ...filters, estado: e.target.value })} data-testid="contratos-filter-estado">
                {ESTADOS.map((e) => <option key={e.v} value={e.v}>{e.l}</option>)}
              </select>
              <select
                className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
                value={filters.tamano || ''}
                onChange={(e) => setFilters({ ...filters, tamano: e.target.value })}
                data-testid="contratos-filter-tamano"
              >
                <option value="">Todos los tamaños</option>
                <option value="muy_grande">🏙️ Muy grande (&gt;500k hab.)</option>
                <option value="grande">🌆 Grande (100k-500k)</option>
                <option value="mediano">🏘️ Mediano (20k-100k)</option>
                <option value="pequeno">🏡 Pequeño (5k-20k)</option>
                <option value="muy_pequeno">🏠 Muy pequeño (&lt;5k)</option>
              </select>
              <select
                className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
                value={filters.vence || ''}
                onChange={(e) => setFilters({ ...filters, vence: e.target.value })}
                data-testid="contratos-filter-vence"
                title="Contratos con fecha de fin próxima, ordenados por urgencia comercial. Los contratos sin fecha_fin no aparecen aquí — usa el re-check en /prospeccion para completarlos."
              >
                <option value="">Vencimiento: cualquiera</option>
                <option value="12">⏰ Vencen en &lt; 12 meses</option>
                <option value="24">📅 Vencen en &lt; 24 meses</option>
                <option value="36">🗓️ Vencen en &lt; 36 meses</option>
              </select>
              <select
                className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
                value={filters.tipo_gestion}
                onChange={(e) => setFilters({ ...filters, tipo_gestion: e.target.value })}
                data-testid="contratos-filter-tipo-gestion"
                title="Tipo de gestión del servicio: municipal, privada, mixta, UTE…"
              >
                <option value="">Tipo de gestión: cualquiera</option>
                <option value="municipal">Municipal (100% pública)</option>
                <option value="privada">Privada (concesión)</option>
                <option value="mixta">Mixta público-privada</option>
                <option value="ute_privada">UTE privada</option>
                <option value="ute_mixta">UTE mixta</option>
                <option value="desconocido">Desconocido</option>
              </select>
              <select
                className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700 md:col-span-3"
                value={filters.gestora_id}
                onChange={(e) => setFilters({ ...filters, gestora_id: e.target.value })}
                data-testid="contratos-filter-gestora"
              >
                <option value="">Todas las gestoras</option>
                {gestoras.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.nombre_comercial || g.razon_social}
                  </option>
                ))}
              </select>
            </div>

            {/* Cascada Zona → Provincia → Municipio */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-ink-100">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold block mb-1">Zona comercial</label>
                <select
                  className="h-10 w-full rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
                  value={filters.zona_id}
                  onChange={(e) => setFilters({ ...filters, zona_id: e.target.value, provincia: '', municipio_ine: '' })}
                  data-testid="contratos-filter-zona"
                >
                  <option value="">Todas las zonas</option>
                  {zonas.map((z) => (
                    <option key={z.id} value={z.id}>{z.nombre} ({(z.provincias || []).length})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold block mb-1">Provincia</label>
                <select
                  className="h-10 w-full rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
                  value={filters.provincia}
                  onChange={(e) => setFilters({ ...filters, provincia: e.target.value, municipio_ine: '' })}
                  data-testid="contratos-filter-provincia"
                >
                  <option value="">{provinciasZona === null ? 'Todas las provincias' : `Todas (${provinciasZona.length})`}</option>
                  {(provinciasZona !== null ? provinciasZona : provincias).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold block mb-1">
                  Municipio
                  {filters.provincia && <span className="text-ink-400 normal-case font-normal ml-1">({municipiosOpciones.length} disponibles en contratos)</span>}
                </label>
                <select
                  className="h-10 w-full rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700 disabled:bg-ink-50 disabled:text-ink-400"
                  value={filters.municipio_ine}
                  onChange={(e) => setFilters({ ...filters, municipio_ine: e.target.value })}
                  disabled={!filters.provincia}
                  data-testid="contratos-filter-municipio"
                >
                  <option value="">{!filters.provincia ? 'Selecciona provincia primero' : 'Todos los municipios'}</option>
                  {municipiosOpciones.map((m) => (
                    <option key={m.codigo_ine} value={m.codigo_ine}>{m.nombre}</option>
                  ))}
                </select>
              </div>
            </div>

            {(filters.zona_id || filters.provincia || filters.municipio_ine) && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-ink-500">
                  Filtrando: {filtered.length} de {contratos.length} contratos
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-7 text-ink-500 hover:text-ink-700"
                  onClick={() => setFilters({ ...filters, zona_id: '', provincia: '', municipio_ine: '' })}
                  data-testid="btn-limpiar-cascada"
                >
                  Limpiar cascada
                </Button>
              </div>
            )}
          </Card>

          <Card className="border-ink-200 overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 border-b border-ink-200 text-ink-600 uppercase text-xs tracking-wider">
                  <tr>
                    <SortableHeader label="Contrato"   sortKey="nombre"     currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                    <SortableHeader label="Gestora"    sortKey="gestora"    currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                    <SortableHeader label="Municipios" sortKey="municipios" currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                    <SortableHeader label="Estado"     sortKey="estado"     currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                    <SortableHeader label="Servicio"   sortKey="servicio"   currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                    <SortableHeader label="Tamaño"     sortKey="tamano"     currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                    <SortableHeader label="Fecha fin"  sortKey="fecha_fin"  currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                    <th className="px-4 py-3 text-right font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100" data-testid="contratos-tbody">
                  {loading && <SkeletonRow cols={8} count={8} />}
                  {!loading && sorted.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-0 py-0">
                        <EmptyState
                          compact
                          icon={FileStack}
                          title="Sin contratos con esos filtros"
                          description="Prueba a limpiar la zona, la provincia o el rango de vencimiento."
                          testid="contratos-empty"
                        />
                      </td>
                    </tr>
                  )}
                  {!loading && sorted.map((c) => (
                    <tr
                      key={c.id}
                      className="hover:bg-ink-50 cursor-pointer"
                      onClick={() => navigate(`/clientes/${c.gestora_id}`)}
                      data-testid={`contrato-row-${c.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                            <FileStack className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-semibold text-ink-900 leading-tight">{c.nombre}</p>
                            {c.codigo_interno && <p className="text-[11px] font-mono text-ink-400 leading-tight">{c.codigo_interno}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-ink-700">
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5 text-ink-400" /> {c.gestora_nombre || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                          {(c.municipios_detalle || []).slice(0, 3).map((m) => (
                            <span key={m.codigo_ine} className="inline-flex items-center gap-1 text-[11px] bg-ink-50 border border-ink-200 text-ink-700 rounded px-1.5 py-0.5">
                              <MapPinned className="w-3 h-3 text-ink-400" /> {m.nombre}
                            </span>
                          ))}
                          {(c.municipios_detalle || []).length > 3 && (
                            <span className="text-[11px] text-ink-500">+{c.municipios_detalle.length - 3}</span>
                          )}
                          {(c.municipios_detalle || []).length === 0 && <span className="text-ink-300 text-xs">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${ESTADO_STYLE[c.estado] || ''} border`}>{c.estado}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        {c.tipo_servicio === 'ambos' ? (
                          <Badge className="bg-violet-50 border-violet-200 text-violet-700 border">💧🚿 Ambos</Badge>
                        ) : c.tipo_servicio === 'saneamiento' ? (
                          <Badge className="bg-amber-50 border-amber-200 text-amber-700 border">🚿 Saneamiento</Badge>
                        ) : (
                          <Badge className="bg-sky-50 border-sky-200 text-sky-700 border">💧 Agua potable</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {c.tamano ? (
                          <Badge className={`border ${
                            c.tamano === 'muy_grande' ? 'bg-violet-100 border-violet-300 text-violet-800' :
                            c.tamano === 'grande' ? 'bg-indigo-100 border-indigo-300 text-indigo-800' :
                            c.tamano === 'mediano' ? 'bg-emerald-100 border-emerald-300 text-emerald-800' :
                            c.tamano === 'pequeno' ? 'bg-sky-50 border-sky-200 text-sky-700' :
                            'bg-ink-50 border-ink-200 text-ink-600'
                          }`}>
                            {c.tamano === 'muy_grande' && '🏙️ Muy grande'}
                            {c.tamano === 'grande' && '🌆 Grande'}
                            {c.tamano === 'mediano' && '🏘️ Mediano'}
                            {c.tamano === 'pequeno' && '🏡 Pequeño'}
                            {c.tamano === 'muy_pequeno' && '🏠 Muy peq.'}
                          </Badge>
                        ) : <span className="text-ink-300">—</span>}
                        {c.habitantes_gestionados ? (
                          <div className="text-[10px] text-ink-400 mt-0.5">{c.habitantes_gestionados.toLocaleString('es-ES')} hab.</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-ink-700 font-mono text-xs">{formatFecha(c.fecha_fin) || <span className="text-ink-300">—</span>}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1 justify-end">
                          {(c.evidencias && c.evidencias.length > 0) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => abrirEvidencias(c)}
                              data-testid={`btn-evidencias-contrato-${c.id}`}
                              title={`Ver ${c.evidencias.length} fuentes verificadas`}
                              className="text-violet-600 hover:bg-violet-50"
                            >
                              <Globe className="w-3.5 h-3.5" />
                              <span className="ml-1 text-[10px] font-semibold">{c.evidencias.length}</span>
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => abrirEditarContrato(c)} data-testid={`btn-editar-contrato-${c.id}`} title="Editar">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={() => borrarContrato(c)} data-testid={`btn-borrar-contrato-${c.id}`} title="Borrar">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
      </>


      {/* Dialog creación rápida desde mapa */}
      <Dialog open={contratoDialog} onOpenChange={(o) => { if (!o) setContratoDialog(false); }}>
        <DialogContent className="max-w-xl" data-testid="dialog-nuevo-contrato-mapa">
          <form onSubmit={guardarContrato}>
            <DialogHeader>
              <DialogTitle>{contratoForm.id ? 'Editar contrato' : 'Nuevo contrato de gestión'}</DialogTitle>
              <DialogDescription>
                {contratoForm.id ? 'Modifica los datos del contrato.' : (contratoMunis.length > 0 ? `Crear contrato que incluye estos municipios: ${contratoMunis.map((m) => m.nombre).join(', ')}.` : 'Completa los datos y añade municipios en el editor inferior.')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-ink-600 mb-1 block">Gestora *</label>
                <select className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3" value={contratoForm.gestora_id} onChange={(e) => setContratoForm({ ...contratoForm, gestora_id: e.target.value })} data-testid="mapa-ct-gestora">
                  <option value="">— Elegir gestora —</option>
                  {gestoras.map((g) => <option key={g.id} value={g.id}>{g.nombre_comercial || g.razon_social} · {g.provincia}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-ink-600 mb-1 block">Nombre *</label>
                <Input value={contratoForm.nombre} onChange={(e) => setContratoForm({ ...contratoForm, nombre: e.target.value })} data-testid="mapa-ct-nombre" />
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Código interno</label>
                <Input value={contratoForm.codigo_interno} onChange={(e) => setContratoForm({ ...contratoForm, codigo_interno: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Estado</label>
                <select className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3" value={contratoForm.estado} onChange={(e) => setContratoForm({ ...contratoForm, estado: e.target.value })}>
                  <option value="prospecto">Prospecto</option>
                  <option value="en_licitacion">En licitación</option>
                  <option value="activo">Activo</option>
                  <option value="prorrogado">Prorrogado</option>
                  <option value="finalizado">Finalizado</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Tipo de servicio</label>
                <select
                  className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3"
                  value={contratoForm.tipo_servicio}
                  onChange={(e) => setContratoForm({ ...contratoForm, tipo_servicio: e.target.value })}
                  data-testid="mapa-ct-tipo-servicio"
                >
                  <option value="agua_potable">💧 Agua potable</option>
                  <option value="saneamiento">🚿 Saneamiento</option>
                  <option value="ambos">💧🚿 Ambos (potable + saneamiento)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Fecha inicio</label>
                <Input type="date" value={contratoForm.fecha_inicio} onChange={(e) => setContratoForm({ ...contratoForm, fecha_inicio: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Fecha fin</label>
                <Input type="date" value={contratoForm.fecha_fin} onChange={(e) => setContratoForm({ ...contratoForm, fecha_fin: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Habitantes gestionados</label>
                <Input
                  type="number" min="0" placeholder="Ej: 45000"
                  value={contratoForm.habitantes_gestionados}
                  onChange={(e) => setContratoForm({ ...contratoForm, habitantes_gestionados: e.target.value })}
                  data-testid="ct-habitantes"
                />
                <p className="text-[10px] text-ink-400 mt-0.5">Tamaño se calcula automáticamente.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block flex items-center gap-2">
                  <input type="checkbox" checked={!!contratoForm.tiene_prorroga} onChange={(e) => setContratoForm({ ...contratoForm, tiene_prorroga: e.target.checked })} data-testid="ct-tiene-prorroga" />
                  ¿Tiene opción a prórroga?
                </label>
                {contratoForm.tiene_prorroga && (
                  <Input
                    type="number" min="0" max="50" placeholder="Años de prórroga"
                    value={contratoForm.prorroga_anos}
                    onChange={(e) => setContratoForm({ ...contratoForm, prorroga_anos: e.target.value })}
                    data-testid="ct-prorroga-anos"
                  />
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-ink-600 mb-1 block">
                  Municipios del contrato ({contratoMunis.length})
                </label>
                {contratoMunis.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2 max-h-24 overflow-y-auto p-2 bg-ink-50 rounded border border-ink-200">
                    {contratoMunis.map((m) => (
                      <span key={m.codigo_ine} className="inline-flex items-center gap-1 text-[11px] bg-white border border-ink-300 text-ink-700 rounded px-1.5 py-0.5">
                        {m.nombre}
                        <button
                          type="button"
                          onClick={() => setContratoMunis(contratoMunis.filter((x) => x.codigo_ine !== m.codigo_ine))}
                          className="ml-1 text-rose-600 hover:text-rose-800"
                          data-testid={`btn-quitar-muni-${m.codigo_ine}`}
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
                <MunicipioPicker onAdd={(m) => {
                  if (!contratoMunis.find((x) => x.codigo_ine === m.codigo_ine)) {
                    setContratoMunis([...contratoMunis, m]);
                  }
                }} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-ink-600 mb-1 block">Notas</label>
                <Textarea rows={2} value={contratoForm.notas} onChange={(e) => setContratoForm({ ...contratoForm, notas: e.target.value })} />
              </div>
              {contratoErr && <div className="sm:col-span-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{contratoErr}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContratoDialog(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-guardar-contrato-mapa">
                {contratoForm.id ? 'Guardar cambios' : 'Crear contrato'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Fuentes verificadas (evidencias del contrato) */}
      <Dialog open={evidenciasDialog} onOpenChange={setEvidenciasDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="dialog-evidencias">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-violet-600" />
              Fuentes verificadas
            </DialogTitle>
            <DialogDescription>
              {evidenciasContrato?.nombre} · {(evidenciasContrato?.evidencias || []).length} evidencia(s) recolectadas durante la prospección automática.
            </DialogDescription>
          </DialogHeader>
          <EvidenciasPanel evidencias={evidenciasContrato?.evidencias || []} />
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={confirmDel.open}
        onOpenChange={(o) => setConfirmDel((s) => ({ ...s, open: o }))}
        resource="contrato"
        itemName={confirmDel.item?.nombre}
        onConfirm={confirmarBorrarContrato}
        testidPrefix="confirm-del-contrato"
      />
    </div>
  );
};

// ---------- Panel de evidencias agrupado por dominio ----------
const EvidenciasPanel = ({ evidencias }) => {
  const grupos = useMemo(() => {
    const map = new Map();
    (evidencias || []).forEach((ev) => {
      let dominio = '';
      try { dominio = new URL(ev.url).hostname.replace(/^www\./, ''); }
      catch { dominio = '(sin dominio)'; }
      if (!map.has(dominio)) map.set(dominio, []);
      map.get(dominio).push(ev);
    });
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [evidencias]);

  if (!evidencias || evidencias.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-ink-400 italic" data-testid="evidencias-vacio">
        Este contrato no tiene fuentes asociadas todavía. Lánzalo desde el módulo de prospección para enriquecerlo automáticamente.
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="evidencias-list">
      {grupos.map(([dominio, items]) => (
        <div key={dominio} className="border border-ink-200 rounded-lg overflow-hidden bg-white">
          <div className="flex items-center gap-2 px-3 py-2 bg-ink-50 border-b border-ink-200">
            <img
              src={`https://www.google.com/s2/favicons?domain=${dominio}&sz=32`}
              alt=""
              className="w-4 h-4"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-sm font-semibold text-ink-800 truncate flex-1">{dominio}</span>
            <Badge className="bg-violet-50 border-violet-200 text-violet-700 border text-[10px]">
              {items.length} {items.length === 1 ? 'fuente' : 'fuentes'}
            </Badge>
          </div>
          <ul className="divide-y divide-ink-100">
            {items.map((ev, i) => (
              <li key={i} className="px-3 py-2 hover:bg-ink-50/50" data-testid={`evidencia-${i}`}>
                <a
                  href={ev.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 break-all"
                >
                  {ev.url} <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
                <div className="text-[11px] text-ink-500 mt-0.5 flex items-center gap-2">
                  {ev.municipio && (
                    <span className="inline-flex items-center gap-0.5">
                      <MapPinned className="w-3 h-3" /> {ev.municipio}
                    </span>
                  )}
                  {ev.fecha_consulta && (
                    <span className="inline-flex items-center gap-0.5">
                      <CalendarClock className="w-3 h-3" /> {ev.fecha_consulta}
                    </span>
                  )}
                  {ev.motor && (
                    <Badge className="bg-ink-50 border-ink-200 text-ink-500 border text-[9px] uppercase">
                      {ev.motor}
                    </Badge>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default ContratosGlobal;


// ---------- Picker interno para añadir municipios al contrato ----------
const MunicipioPicker = ({ onAdd }) => {
  const [q, setQ] = React.useState('');
  const [resultados, setResultados] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!q || q.length < 2) { setResultados([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await municipiosApi.list({ q, limit: 10 });
        const items = Array.isArray(r) ? r : (r.items || []);
        setResultados(items);
      } catch { setResultados([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar municipio por nombre…"
          className="pl-7 h-9 text-sm"
          data-testid="muni-picker-input"
        />
      </div>
      {loading && <div className="text-xs text-ink-400 mt-1">Buscando…</div>}
      {!loading && resultados.length > 0 && (
        <div className="mt-1 max-h-40 overflow-y-auto border border-ink-200 rounded bg-white">
          {resultados.map((m) => (
            <button
              key={m.codigo_ine}
              type="button"
              onClick={() => { onAdd({ codigo_ine: m.codigo_ine, nombre: m.nombre, provincia: m.provincia }); setQ(''); setResultados([]); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-brand-50 border-b border-ink-100 last:border-0"
              data-testid={`muni-picker-item-${m.codigo_ine}`}
            >
              {m.nombre} <span className="text-ink-400 text-xs">· {m.provincia}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
